import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { hasInAppHistory } from '../hooks/useBack';

interface BackHeaderProps {
  to?: string;
  label: string;
  title?: string;
  subtitle?: string;
  showFlag?: boolean;
  flag?: string;
  rightAction?: React.ReactNode;
  className?: string;
  preserveState?: boolean;
}

export const BackHeader: React.FC<BackHeaderProps> = ({
  to,
  label,
  title,
  subtitle,
  showFlag = true,
  flag,
  rightAction,
  className = '',
  preserveState = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useAuth();
  const countryFlag = flag ?? (company?.country === 'CA' ? '🇨🇦' : '🇺🇸');

  const isShellNav = !to;

  // `to` is where Back goes when there is nothing behind this screen. With
  // history, Back is a real Back: pushing `to` made list → detail → list, and
  // the list's own Back then returned here instead of leaving.
  const handleBack = () => {
    if (!to) {
      navigate('/');
    } else if (hasInAppHistory(location.key)) {
      navigate(-1);
    } else {
      navigate(to, {
        replace: true,
        state: preserveState ? location.state : undefined
      });
    }
  };

  return (
    <div className={`border-b border-border px-6 py-4 ${className}`}>
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors"
        aria-label={isShellNav ? 'Go to Dashboard' : `Back to ${label}`}
      >
        {isShellNav ? <Home size={24} /> : <ArrowLeft size={24} />}
        {isShellNav ? 'Dashboard' : 'Back'}
      </button>

      <div className="flex items-center justify-between mt-2">
        <div>
          {title && (
            <h1 className="text-base font-bold text-foreground leading-tight">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="text-sm text-fieldiq-text mt-0.5 truncate">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {rightAction}
        </div>
      </div>
    </div>
  );
};

export const CompactBackHeader: React.FC<{
  to?: string;
  label: string;
  showFlag?: boolean;
}> = ({ to, label, showFlag = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useAuth();
  const isShellNav = !to;

  const handleBack = () => {
    if (!to) {
      navigate('/');
    } else if (hasInAppHistory(location.key)) {
      navigate(-1);
    } else {
      navigate(to, { replace: true, state: location.state });
    }
  };

  return (
    <div className="border-b border-border px-6 py-4">
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors"
        aria-label={isShellNav ? 'Go to Dashboard' : `Back to ${label}`}
      >
        {isShellNav ? <Home size={24} /> : <ArrowLeft size={24} />}
        {isShellNav ? 'Dashboard' : 'Back'}
      </button>
    </div>
  );
};

export default BackHeader;
